import { pb } from './pb'

/**
 * Membership billing, read and driven from the portal.
 *
 * These are custom JSVM routes on PocketBase rather than collection records, so they take the
 * token as a bare `Authorization` header and are called with `fetch` rather than through the SDK.
 * The shapes here match what `backend/pb_public/join/index.html` already consumes, because that
 * page and this one talk to the same endpoints and must not disagree about what a subscription is.
 *
 * Checkout is deliberately not here. The Stripe payment links in the join page are still
 * `REPLACE-WITH-YEARLY-LINK` placeholders, so there is no live purchase path to absorb yet; only
 * management of an existing subscription is real.
 */

const BASE = 'https://api.torahly.app'

export interface Subscription {
  interval?: 'month' | 'year' | string
  intervalCount?: number
  status?: string
  /** In minor units, and genuinely nullable: a comped account has no amount. */
  amount?: number | null
  currency?: string
  /** Unix seconds. */
  currentPeriodEnd?: number
  cancelAtPeriodEnd?: boolean
  cardBrand?: string
  cardLast4?: string
}

/**
 * The live subscription, or null when there is nothing to show.
 *
 * Null covers three different situations on purpose: no subscription, an endpoint that is not
 * deployed, and a request that failed. None of them should put an error in front of a woman who
 * only opened her account page, and the membership flag on her record already answers the
 * question that matters.
 */
export async function fetchSummary(): Promise<Subscription | null> {
  const token = pb.authStore.token
  if (!token) return null
  try {
    const response = await fetch(`${BASE}/api/membership/summary`, {
      headers: { Authorization: token },
    })
    if (!response.ok) return null
    const body = (await response.json()) as { subscription?: Subscription }
    return body.subscription ?? null
  } catch {
    return null
  }
}

/** Cancels at period end, or restarts a membership that was set to end. Throws on failure. */
export async function setCancellation(cancel: boolean): Promise<void> {
  const token = pb.authStore.token
  if (!token) throw new Error('Not signed in')

  const response = await fetch(`${BASE}/api/membership/${cancel ? 'cancel' : 'resume'}`, {
    method: 'POST',
    headers: { Authorization: token, 'Content-Type': 'application/json' },
    body: '{}',
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error || "That didn't go through. Please try again.")
  }
}

export function money(minorUnits: number, currency = 'usd'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(minorUnits / 100)
  } catch {
    return (minorUnits / 100).toFixed(2)
  }
}

export function fromUnix(seconds: number | undefined): string | null {
  if (!seconds) return null
  const date = new Date(Number(seconds) * 1000)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

/** "Yearly", "Monthly, trial", "Every 3 months". */
export function planName(subscription: Subscription): string {
  const base =
    (subscription.intervalCount ?? 1) > 1
      ? `Every ${subscription.intervalCount} ${subscription.interval}s`
      : subscription.interval === 'year'
        ? 'Yearly'
        : subscription.interval === 'month'
          ? 'Monthly'
          : 'Membership'
  return subscription.status === 'trialing' ? `${base}, trial` : base
}

/** "$29.99 a year", "$3.99 a month", or null when there is no amount to state. */
export function priceLine(subscription: Subscription): string | null {
  if (subscription.amount === null || subscription.amount === undefined) return null
  const every =
    (subscription.intervalCount ?? 1) > 1
      ? `every ${subscription.intervalCount} ${subscription.interval}s`
      : subscription.interval === 'year'
        ? 'a year'
        : 'a month'
  return `${money(subscription.amount, subscription.currency)} ${every}`
}
